import React, { useState } from "react";
import { bookImageSrc, bookImageFallback } from "./config";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/BookDetail.css";

const BookDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { book } = location.state || {};

  const [toast, setToast] = useState("");

  // ✅ Check if book is purchased
  const purchasedBooks = JSON.parse(localStorage.getItem("purchasedBooks")) || [];
  const isPurchased = purchasedBooks.some(
    (item) => item.pdf_name === book?.pdf_name
  );

  // ✅ Check if user is superadmin
  const userRole = localStorage.getItem("userRole");
  const interests = JSON.parse(localStorage.getItem("selectedInterests") || "[]");
  const isSuperAdmin = userRole === "superadmin" || interests.includes("All");

  // ✅ Check if book should be free based on admin settings
  const isBookFreeByAdmin = () => {
    if (!book || isSuperAdmin) return false; // Superadmin doesn't get admin free books
    
    const adminFreeBookSettings = JSON.parse(localStorage.getItem("adminFreeBookSettings") || "{}");
    const category = book.category || book.core || book.source || "";
    const freeSettings = adminFreeBookSettings[category];
    
    if (!freeSettings || !freeSettings.enabled) return false;
    
    // Check if all books in category are free
    if (freeSettings.allFree) return true;
    
    // Check if this specific book is in the free list
    return freeSettings.freeBooks && freeSettings.freeBooks.includes(book.pdf_name);
  };

  // Determine if book is actually free (original free OR admin set free)
  const isActuallyFree = (!book?.price || book?.price === "0") || isBookFreeByAdmin();

  // Function to get appropriate rating display
  const getRatingDisplay = (rating) => {
    if (!rating || rating === "0") return "No ratings yet";
    const numRating = parseFloat(rating);
    const stars =
      "★".repeat(Math.floor(numRating)) +
      "☆".repeat(5 - Math.floor(numRating));
    return `${stars} ${numRating}`;
  };

  // Function to format duration
  const formatDuration = (duration) => {
    if (!duration) return "Not specified";
    return duration.includes("h") ? duration : `${duration}`;
  };

  if (!book) {
    return (
      <div className="bd-book-detail-page">
        <div className="bd-header">
          <div className="bd-breadcrumb">
            <span>Library</span>
            <span className="bd-separator">›</span>
            <span>Book Details</span>
          </div>
        </div>
        <div className="bd-content">
          <div className="bd-no-book">
            <h2>No Book Selected</h2>
            <p>Please select a book to view its details.</p>
            <button className="bd-back-btn" onClick={() => navigate(-1)}>
              Back to Library
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Add to Cart handler with toast
  const handleAddToCart = () => {
    let cart = JSON.parse(localStorage.getItem("cart")) || [];
    const alreadyInCart = cart.some((item) => item.pdf_name === book.pdf_name);

    if (alreadyInCart) {
      setToast("This book is already in your cart!");
    } else {
      cart.push(book);
      localStorage.setItem("cart", JSON.stringify(cart));
      window.dispatchEvent(new Event("storage"));
      setToast("Successfully added to cart!");
    }

    setTimeout(() => setToast(""), 3000);
  };

  // Add to Wishlist handler with toast
  const handleAddToWishlist = () => {
    let wishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
    const alreadyInWishlist = wishlist.some(
      (item) => item.pdf_name === book.pdf_name
    );

    if (alreadyInWishlist) {
      setToast("This book is already in your wishlist!");
    } else {
      wishlist.push(book);
      localStorage.setItem("wishlist", JSON.stringify(wishlist));
      setToast("Successfully added to wishlist!");
    }

    setTimeout(() => setToast(""), 3000);
  };

  return (
    <div className="bd-book-detail-page">
      {/* Header Section */}
      <div className="bd-header">
        <div className="bd-hero-content">
          <div className="bd-hero-left">
            <h1 className="bd-book-title">{book.pdf_name}</h1>
            <p className="bd-book-subtitle">
              {book.discription && book.discription.trim() !== ""
                ? book.discription
                : "Discover valuable insights and knowledge in this comprehensive guide designed to enhance your understanding and skills."}
            </p>
            <div className="bd-info-row">
              <div className="bd-book-meta">
                <span className="bd-meta-item">
                  {book.lessons || "Not specified"} lessons
                </span>
                <span className="bd-meta-divider"></span>
                <span className="bd-meta-item">
                  {formatDuration(book.duration)}
                </span>
              </div>
              <div className="bd-premium-badge">
                <div className="bd-premium-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M13 2.05v2.02c4.39.54 7.5 4.53 6.96 8.92-.46 3.64-3.32 6.5-6.96 6.96v2.02c5.5-.55 9.5-5.43 8.95-10.93-.47-4.75-4.2-8.48-8.95-8.99z" />
                    <path d="M12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7c1.93 0 3.68.79 4.95 2.05l-1.41 1.41C14.63 7.56 13.38 7 12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5h2c0 3.87-3.13 7-7 7z" />
                  </svg>
                </div>
                <div className="bd-premium-content">
                  <div className="bd-premium-label">Premium</div>
                  <p className="bd-premium-text">
                    Access this top-rated course, plus 26,000+ more, with a SkillMaster plan.
                  </p>
                </div>
                <div className="bd-rating-section">
                  <div className="bd-rating-stars">
                    {getRatingDisplay(book.rating)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="bd-right-sidebar">
            <div className="bd-subscription-card">
              <div className="bd-preview-card">
                <img
                  src={bookImageSrc(book, 320, 180)}
                  alt={book.pdf_name}
                  className="bd-preview-image"
                  onError={bookImageFallback(book, 320, 180)}
                />
                <div className="bd-preview-overlay"></div>
              </div>

              {/* Pricing */}
              <div className="bd-pricing">
                {isActuallyFree ? (
                  <>
                    <span className="bd-price">Free</span>
                    {isBookFreeByAdmin() && book.price && book.price !== "0" && (
                      <span className="bd-original-price" title="Admin Free">
                        ₹{book.price}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="bd-price">₹{book.price}</span>
                    {book.original_price && (
                      <span className="bd-original-price">
                        ₹{book.original_price}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* ✅ Action Buttons */}
              <div className="bd-action-buttons">
                {isActuallyFree || isPurchased ? (
                  <button
                    className="bd-add-to-cart-btn"
                    onClick={() => {
                      if (book.pdf_link) {
                        window.open(book.pdf_link, "_blank");
                      } else {
                        alert("PDF link not available");
                      }
                    }}
                  >
                    {isPurchased ? "Start Reading" : isBookFreeByAdmin() ? "Start Reading" : "Start Reading (Free)"}
                  </button>
                ) : (
                  <button
                    className="bd-add-to-cart-btn"
                    onClick={handleAddToCart}
                  >
                    Add to cart
                  </button>
                )}

                {/* Wishlist */}
                <button className="bd-wishlist-btn" onClick={handleAddToWishlist}>
                  ♡
                </button>
              </div>

              {/* ✅ Buy Now button only if not purchased and not admin free */}
              {!isActuallyFree && !isPurchased && (
                <button
                  className="bd-buy-now-btn"
                  onClick={() => navigate("/checkout", { state: { book } })}
                >
                  Buy now
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bd-content">
        <div className="bd-main-section">
          <div className="bd-left-content"></div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && <div className="toast-message">{toast}</div>}
    </div>
  );
};

export default BookDetail;